/**
 * Per-page metadata for a single-page app.
 *
 * public/index.html carries a full, correct set of tags for the home page, which is
 * what a crawler or a link unfurler sees before any JavaScript runs. Once the app is
 * live those tags describe whatever page the visitor navigated to instead — this
 * module is what rewrites them, so a link copied out of the address bar on a product
 * page unfurls as that product rather than as the home page.
 *
 * Everything is derived from `company` so the site's identity is still stated in one
 * place; nothing here restates the brand.
 */
import { company } from '@/data/company';

const SITE_NAME = company.name;
const DEFAULT_TITLE = `${SITE_NAME} — ${company.shortTagline}`;
const DEFAULT_DESCRIPTION = company.description;
const DEFAULT_IMAGE = `${company.url}/og-image.jpg`;

/**
 * Titles are written without the brand; addBrand() appends it. Keeping the suffix out
 * of the table means the brand appears exactly once and can never be forgotten on a
 * new entry.
 *
 * `robots: 'noindex'` marks pages that must never reach a search result — a cart tied
 * to one browser session, an account area, a checkout, the auth screens. They are not
 * secret (the API guards the data), but indexing them wastes crawl budget on pages
 * that are empty or nonsensical to anyone arriving from a search.
 */
const PAGE_SEO = {
  '/': {
    title: null, // The home page is the brand; it does not get a "X — Brand" suffix.
    description: DEFAULT_DESCRIPTION,
  },
  '/index-v2': { sameAs: '/' },
  '/about': {
    title: 'About Us',
    description:
      `${SITE_NAME} is a Lebanese furniture workshop in Ghazir. Meet the team behind our sofas, dining sets, bedrooms and full interior fit-outs.`,
  },
  '/shop-v2': {
    title: 'Shop Furniture',
    description:
      'Browse sofas, armchairs, dining tables, beds and office furniture — in stock and made to order, delivered across Lebanon.',
  },
  '/portfolio-v1': {
    title: 'Our Work',
    description:
      'Completed furniture and interior projects: homes, restaurants and offices fitted out by our Ghazir workshop.',
  },
  '/request-quotation': {
    title: 'Request a Quotation',
    description:
      'Tell us about the piece you have in mind — dimensions, materials and budget — and we will send you an invoice for it.',
  },
  '/assistant': {
    title: 'Design Assistant',
    description:
      'Describe your room or upload a photo, and get furniture suggestions and a quotation from our design assistant.',
  },
  '/faq': {
    title: 'Frequently Asked Questions',
    description:
      'Delivery, lead times, custom orders, materials and payment — the questions we are asked most often.',
  },
  '/contact': {
    title: 'Contact Us',
    description: `Visit the workshop in ${company.address}, call ${company.phone}, or send us a message.`,
  },
  '/terms-and-conditions': {
    title: 'Terms & Conditions',
    description: `The terms that apply to orders placed with ${SITE_NAME}.`,
  },

  // Everything below is per-visitor or transactional — useless in a search result.
  '/cart': { title: 'Your Cart', robots: 'noindex, follow' },
  '/checkout': { title: 'Checkout', robots: 'noindex, follow' },
  '/login': { title: 'Log In', robots: 'noindex, follow' },
  '/register': { title: 'Create an Account', robots: 'noindex, follow' },
  '/forger-password': { title: 'Forgot Password', robots: 'noindex, follow' },
  '/reset-password': { title: 'Reset Password', robots: 'noindex, nofollow' },
  '/thank-you': { title: 'Thank You', robots: 'noindex, nofollow' },
  '/my-profile': { title: 'My Profile', robots: 'noindex, nofollow' },
  '/my-account': { title: 'My Account', robots: 'noindex, nofollow' },
  '/edit-account': { title: 'Edit Account', robots: 'noindex, nofollow' },
  '/order-history': { title: 'Order History', robots: 'noindex, nofollow' },
  '/my-requests': { title: 'My Requests', robots: 'noindex, nofollow' },
  '/wishlist': { title: 'Wishlist', robots: 'noindex, nofollow' },
  '/error': { title: 'Page Not Found', robots: 'noindex, nofollow' },
};

// Routes whose content is fetched after navigation. The router applies the fallback
// below immediately so the tab is never left showing the previous page's title, and
// the view calls setSeo() again once it knows what it is actually displaying.
const DYNAMIC_FALLBACK = {
  '/product-details': { title: 'Product', description: DEFAULT_DESCRIPTION },
  '/portfolio-details-v1': { title: 'Project', description: DEFAULT_DESCRIPTION },
};

function addBrand(title) {
  return title ? `${title} — ${SITE_NAME}` : DEFAULT_TITLE;
}

/**
 * Meta tags are matched on `name` for most of the standard set and on `property` for
 * Open Graph, which is the whole reason this takes a selector rather than a key: an
 * `og:` tag written as name="og:title" is ignored by Facebook and LinkedIn.
 */
function setMeta(selector, attribute, key, content) {
  if (typeof document === 'undefined') return;

  let tag = document.head.querySelector(`meta[${selector}="${key}"]`);

  if (!content) {
    // A page without a value must not inherit the previous page's — remove it.
    if (tag) tag.remove();
    return;
  }

  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }

  tag.setAttribute('content', content);
}

function setLink(rel, href) {
  if (typeof document === 'undefined') return;

  let tag = document.head.querySelector(`link[rel="${rel}"]`);

  if (!href) {
    if (tag) tag.remove();
    return;
  }

  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', rel);
    document.head.appendChild(tag);
  }

  tag.setAttribute('href', href);
}

/**
 * Apply one page's metadata. Every field is optional; anything omitted falls back to
 * the site default rather than being left as whatever the previous page set.
 *
 * @param {object}  meta
 * @param {string}  [meta.title]        Page title without the brand suffix.
 * @param {string}  [meta.description]
 * @param {string}  [meta.image]        Absolute URL for the share card.
 * @param {string}  [meta.path]         Path for the canonical URL, e.g. "/shop-v2".
 * @param {string}  [meta.robots]       e.g. "noindex, follow".
 * @param {string}  [meta.type]         Open Graph type; "website" or "product".
 */
export function setSeo(meta = {}) {
  if (typeof document === 'undefined') return;

  const title = addBrand(meta.title);
  const description = meta.description || DEFAULT_DESCRIPTION;
  const image = meta.image || DEFAULT_IMAGE;
  // Query strings are deliberately dropped: ?category=2 and ?page=3 are the same
  // document filtered, and letting each combination self-canonicalise splits the
  // page's ranking across dozens of near-duplicate URLs.
  const canonical = `${company.url}${meta.path ? meta.path.split('?')[0] : ''}`;

  document.title = title;

  setMeta('name', 'name', 'description', description);
  setMeta('name', 'name', 'robots', meta.robots || 'index, follow');

  setMeta('property', 'property', 'og:title', title);
  setMeta('property', 'property', 'og:description', description);
  setMeta('property', 'property', 'og:image', image);
  setMeta('property', 'property', 'og:url', canonical);
  setMeta('property', 'property', 'og:type', meta.type || 'website');
  setMeta('property', 'property', 'og:site_name', SITE_NAME);

  setMeta('name', 'name', 'twitter:card', 'summary_large_image');
  setMeta('name', 'name', 'twitter:title', title);
  setMeta('name', 'name', 'twitter:description', description);
  setMeta('name', 'name', 'twitter:image', image);

  setLink('canonical', canonical);
}

/**
 * Called from the router on every navigation. Resolves the entry for the route and
 * hands it to setSeo(); a path with no entry still gets the site defaults, so a page
 * added later is described correctly rather than inheriting the last page's tags.
 */
export function applyRouteSeo(route) {
  const path = route.path;
  let entry = PAGE_SEO[path];

  // Aliases (/index-v2 is the home page under a second path) borrow the target's
  // copy so the two can never describe themselves differently.
  if (entry?.sameAs) {
    entry = { ...PAGE_SEO[entry.sameAs], canonicalPath: entry.sameAs };
  }

  if (!entry) {
    const dynamic = Object.keys(DYNAMIC_FALLBACK).find((prefix) => path.startsWith(`${prefix}/`));
    entry = dynamic ? DYNAMIC_FALLBACK[dynamic] : {};
  }

  setSeo({ ...entry, path: entry.canonicalPath || path });
}

export const seoDefaults = {
  siteName: SITE_NAME,
  title: DEFAULT_TITLE,
  description: DEFAULT_DESCRIPTION,
  image: DEFAULT_IMAGE,
  url: company.url,
};
